import { isDefined } from '@sgme/fp';
import { useBatchNavigationContext } from 'components/common-form/contexts/batch/BatchNavigationContext';
import { NumericFieldsContext } from 'components/common-form/contexts/NumericFieldsContext';
import { useBlockOrFieldReadOnly } from 'components/common-form/hooks/useBlockOrFieldReadOnly';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { useHighlightColor } from 'components/common-form/hooks/useHighlightColor';
import { useIsFieldInvalid } from 'components/common-form/hooks/useIsFieldInvalid';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import type { PercentProps } from 'components/shared/Percent/model';
import { PercentWithUnit } from 'components/shared/Percent/PercentWithUnit';
import { withIncrementers } from 'components/shared/Percent/WithIncrementers';
import { WithMinMax } from 'components/shared/WithMinMax';
import type { WithUnitProps } from 'components/shared/WithUnit/WithUnit';
import type { IntlKey } from 'locales';
import type { PercentFieldData, PercentFieldMetadata } from 'models/forms/fields';
import { FC, useContext, useMemo } from 'react';
import { getTextAlignClassName } from 'shared/utils/getTextAlignClassName';

import type { ProductNodeBuilderProps } from '../ProductNodeBuilder.model';

export const PercentField: FC<ProductNodeBuilderProps> = ({ node: { id }, workspaceType, tabIndex }) => {
  const [value, setValue] = useData<PercentFieldData>(id);
  const isFieldInvalid = useIsFieldInvalid(id);
  const isReadOnly = useBlockOrFieldReadOnly(id);
  const patchedFieldClassName = usePatchedFieldHighlight(id);
  const metadata = useFieldMetadata<PercentFieldMetadata>(id)!;
  const {
    onEditingKeyDown: onKeyDown,
    lostInputBeforeEditing,
    decimalPadding,
    isCellInactive,
    isFocused,
    isSelectAll,
  } = useBatchNavigationContext({ decimalPadding: metadata.decimalPadding });

  const highlightColor = useHighlightColor();
  const numericFieldCtx = useContext(NumericFieldsContext);
  const lostInputValue = useMemo<number | undefined>(() => {
    return lostInputBeforeEditing ? Number(lostInputBeforeEditing) : undefined;
  }, [lostInputBeforeEditing]);

  if (isDefined(value) && typeof value !== 'number') {
    return null;
  }

  const props: PercentProps & WithUnitProps = {
    e2e: id,
    unit: metadata.unit ?? '%',
    precision: metadata.nbDecimals,
    decimalPadding: decimalPadding,
    value: lostInputValue ?? value,
    onBlur: (val?: number) => {
      const newValue: Nullable<number> = val ?? null;
      if (newValue !== value) {
        setValue(newValue);
      }
    },
    isDisabled: workspaceType === 'newBatch' ? isReadOnly : false,
    isReadOnly,
  };
  const tooltipKey = metadata.hasTooltip ? (`form.${metadata.translationKey}.tooltip` as IntlKey) : undefined;
  const textAlignClassName = getTextAlignClassName(metadata.textAlign);
  const PercentFieldComponent = numericFieldCtx.useIncrementers
    ? withIncrementers(PercentWithUnit, { layout: numericFieldCtx.incrementersLayout })
    : PercentWithUnit;
  return (
    <BlockOrientationFormGroup
      inputId={id}
      label={`form.${metadata.translationKey}` as any}
      workspaceType={workspaceType}
      minWidth={metadata.minWidth}
      maxWidth={metadata.maxWidth}
      tooltipLocation={metadata.tooltipLocation}
      tooltipKey={tooltipKey}
      className={patchedFieldClassName}
    >
      <WithMinMax
        Component={PercentFieldComponent}
        hasError={isFieldInvalid}
        highlightColor={metadata.isHighlighted ? highlightColor : undefined}
        className={textAlignClassName}
        tabIndex={tabIndex}
        isCellInactive={isCellInactive}
        onKeyDown={onKeyDown}
        isFocused={isFocused}
        isSelectAll={isSelectAll}
        minimum={metadata.minimum}
        maximum={metadata.maximum}
        {...props}
      />
    </BlockOrientationFormGroup>
  );
};

PercentField.displayName = 'PercentField';
